import {Fragment, useEffect, useMemo, useState} from 'react';
import {Link, useLocation} from 'react-router-dom';
import {DefaultMetaTags} from '@common/seo/default-meta-tags';
import {SiteHeader} from '@common/ui/navigation/site-header';
import {useAuth} from '@common/auth/use-auth';
import {Subscription} from '@common/billing/subscription';
import {Footer} from '@common/ui/footer/footer';
import {resolveSiteLogoSrc, SITE_LOGO_ALT} from '@common/branding/site-logo';
import {useSettings} from '@common/core/settings/use-settings';
import {useThemeSelector} from '@common/ui/themes/theme-selector-context';
const FILESHIMMER_LOGO = '/images/homepage/fileshimmer-logo.png';
const FILESHIMMER_LOGO_DARK = '/FileShimmer_logo.png';
const CT20_RELEASE_DATE = new Date('2026-01-01T00:00:00Z');

function getActiveSubscription(subscriptions?: Omit<Subscription, 'user'>[]) {
  if (!subscriptions?.length) return null;
  return (
    subscriptions.find(sub => (sub.valid || sub.active) && !sub.cancelled) ??
    subscriptions[0]
  );
}

function isLegacySubscription(subscription?: Omit<Subscription, 'user'> | null) {
  if (!subscription?.created_at) return false;
  const startedAt = new Date(subscription.created_at);
  return !Number.isNaN(startedAt.getTime()) && startedAt < CT20_RELEASE_DATE;
}

function getStorageText(subscription?: Omit<Subscription, 'user'> | null) {
  const featureList = subscription?.product?.feature_list || [];
  const matchedStorage = featureList.find(item =>
    /\b\d+(\.\d+)?\s*(gb|tb)\b.*\bstorage\b/i.test(item)
  );
  return matchedStorage || '50gb storage';
}

function getPriceText(subscription?: Omit<Subscription, 'user'> | null) {
  const amount = subscription?.price?.amount ?? 10;
  const currency = subscription?.price?.currency || 'USD';
  const interval = subscription?.price?.interval || 'month';
  const intervalCount = subscription?.price?.interval_count || 1;
  const formatted = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    currencyDisplay: 'narrowSymbol',
    maximumFractionDigits: 2,
  }).format(amount / intervalCount);
  const intervalLabel = interval === 'month' ? 'mo' : interval;
  return `${formatted}/${intervalLabel}`;
}

export function LandingPage() {
  const {hash} = useLocation();
  const {user, isLoggedIn, isSubscribed, hasPermission} = useAuth();
  const {branding} = useSettings();
  const {selectedTheme} = useThemeSelector();
  const logoSrc = resolveSiteLogoSrc(branding, !!selectedTheme?.is_dark);
  const isDarkMode = !!selectedTheme?.is_dark;
  const fileshimmerLogoSrc = isDarkMode ? FILESHIMMER_LOGO_DARK : FILESHIMMER_LOGO;
  const [isPopupDismissed, setIsPopupDismissed] = useState(false);

  useEffect(() => {
    if (hash.replace(/^#/, '') !== 'primary-features') return;
    const section = document.getElementById('primary-features');
    if (!section) return;
    requestAnimationFrame(() => {
      section.scrollIntoView({behavior: 'smooth', block: 'start'});
    });
  }, [hash]);

  const activeSubscription = useMemo(
    () => getActiveSubscription(user?.subscriptions),
    [user?.subscriptions]
  );
  const isLegacySubscriber =
    isLoggedIn && isSubscribed && isLegacySubscription(activeSubscription);
  const isAdmin = hasPermission('admin.access');
  const popupKey = `ct20-pricing-popup-dismissed-${
    activeSubscription?.id ?? (isAdmin ? 'admin' : 'none')
  }`;
  const canSeeLegacyPopup = isLegacySubscriber || isAdmin;

  useEffect(() => {
    if (!canSeeLegacyPopup) return;
    const wasDismissed = localStorage.getItem(popupKey) === '1';
    setIsPopupDismissed(wasDismissed);
  }, [canSeeLegacyPopup, popupKey]);

  const showLegacyPopup = canSeeLegacyPopup && !isPopupDismissed;
  const oldPlanText = `${getPriceText(activeSubscription)}: 10gb storage`;
  const newPlanText = `${getPriceText(activeSubscription)}: ${getStorageText(
    activeSubscription
  )}`;
  const greetingName = user?.first_name || user?.display_name || 'there';

  const dismissPopup = () => {
    setIsPopupDismissed(true);
    localStorage.setItem(popupKey, '1');
  };

  const insetCardClassName =
    'rounded-[60px] border-[10px] border-white bg-[#e9ecf0] px-[24px] py-[3rem] shadow-[1px_1px_50px_5px_rgba(144,144,144,0.25)] dark:shadow-none dark:border-[#30343b] dark:bg-[#242933]';

  return (
    <Fragment>
      <DefaultMetaTags />
      <main className="min-h-screen bg-[#f5f5f7] text-[#1a1a1a] dark:bg-[#14161a] dark:text-[#f3f4f6]">
        <SiteHeader />
        <div className="relative mx-auto w-full max-w-[1895px] px-[6px] pb-[24px] lg:pb-[25px]">
          {showLegacyPopup && (
            <div className="absolute right-[18px] top-[10px] z-30 w-[246px] sm:w-[320px] lg:right-[20px] lg:top-[12px]">
              <div className="relative rounded-[16px] border border-[#d8562a] bg-[#eceef3] px-[18px] pb-[16px] pt-[14px] shadow-[0_10px_20px_rgba(15,23,42,0.1)] dark:bg-[#1f232b]">
                <button
                  type="button"
                  onClick={dismissPopup}
                  aria-label="Close"
                  className="absolute right-[-14px] top-[-14px] flex h-[28px] w-[28px] items-center justify-center rounded-full bg-[#d8562a] text-[18px] leading-none text-white"
                >
                  ×
                </button>
                <p className="text-[15px] font-normal text-[#6a7480] dark:text-[#b3bcc8]">
                  Hey {greetingName}!
                </p>
                <p className="mt-[10px] text-[14px] leading-[1.35] text-[#6a7480] dark:text-[#b3bcc8]">
                  We updated our site and changed our pricing. Your plan has
                  updated automatically.
                </p>
                <p className="mt-[10px] text-[14px] font-semibold text-[#535353] dark:text-[#d5d9e0]">
                  Your old plan: {oldPlanText}
                </p>
                <p className="mt-[4px] text-[14px] font-semibold text-[#3c7d44]">
                  Your new plan: {newPlanText}
                </p>
                <Link
                  to="/pricing"
                  className="mt-[14px] inline-flex h-[38px] items-center justify-center rounded-[14px] bg-primary px-[18px] text-[14px] font-medium text-white transition-colors hover:bg-primary/90"
                >
                  Learn more
                </Link>
              </div>
            </div>
          )}

          <section id="primary-features" aria-label="CloudTransfer and FileShimmer hero" className="mx-auto mt-[22px] grid w-full max-w-[1873px] grid-cols-1 overflow-hidden rounded-[60px] bg-[#e9ecf0] dark:bg-[#1d2128] hero:mt-[41px] hero:grid-cols-[43%_minmax(0,1fr)]"
          >
            <div className="bg-[#e9ecf0] px-[24px] pb-[34px] pt-[28px] text-center dark:bg-[#1d2128] sm:px-[36px] hero:min-h-[756px] hero:rounded-l-[60px] hero:px-[24px] hero:pb-[24px] hero:pt-[28px]">
              <div className="mx-auto flex max-w-[620px] flex-col items-center hero:max-w-none">
                <img src={logoSrc} alt={SITE_LOGO_ALT} className="h-auto w-full max-w-[463px] object-contain hero:mt-[54px]"
                />
                <p className="mt-[5px] mb-[12px] rounded-full bg-white px-[11px] pb-[5px] pt-[1px] font-['Inria_Sans',sans-serif] text-[30px] font-light leading-none text-[#1a1a1a] dark:bg-[#242933] dark:text-[#f3f4f6] sm:text-[34px] hero:h-[50px] hero:w-[50px] hero:text-[43px]">
                  x
                </p>
                <img src={fileshimmerLogoSrc} alt="FileShimmer" className="mt-[3px] h-auto w-full max-w-[384px] object-contain mix-blend-darken dark:mix-blend-normal"
                />
                 {/* <h1 className="font-['Inter',sans-serif] text-[40px] font-black leading-none text-black sm:text-[48px] hero:text-[60px]">
                  FileShimmer
                  </h1> */}
                <p className="mt-[28px] max-w-[464px] text-left text-[18px] font-normal leading-[1.05] text-[#6a7480] dark:text-[#b3bcc8] sm:text-[20px] hero:mt-[22px]">
                  With the features of file transferring, combined with secure,
                  distributed storage. We're building for a simple world.
                </p>

                <div className="mt-[54px] w-full max-w-[510px] hero:mt-[128px]">
                  <h2 className="font-['Inter',sans-serif] text-left text-[32px] font-bold leading-[1.2] text-[#1a1a1a] dark:text-[#f3f4f6] sm:text-[40px]">
                    Secure &amp; Simple Storage
                  </h2>
                  <p className="mt-[8px] max-w-[510px] text-left text-[18px] font-normal leading-[1.08] text-[#6a7480] dark:text-[#b3bcc8] sm:text-[20px] hero:mt-[11px]">
                    <span className="font-bold italic text-[#d8562a]">
                      No
                    </span>
                    {' '}data selling.{' '}
                    <span className="font-bold italic text-[#d8562a]">
                      Ad-free
                    </span>{' '}
                    always and{' '}
                    <span className="font-bold italic text-[#d8562a]">
                      never AI built-in
                    </span>
                    . Your files stay{' '}
                    <span className="font-bold italic text-[#d8562a]">
                      yours
                    </span>{' '}
                    across a decentralized network.
                  </p>
                  <Link
                    to="/register"
                    className="mt-[18px] inline-flex h-[60px] w-[141px] items-center justify-center rounded-[24px] bg-[#2b7e20] text-[17px] font-medium leading-normal text-white transition-colors duration-200 hover:bg-[#3c7d44] hero:mt-[21px] min-[1280px]:max-[1700px]:mt-[30px]"
                  >
                    Try for free
                  </Link>
                </div>
              </div>
            </div>

             <div className="grid gap-42 bg-[#e9ecf0] px-[24px] pb-[36px] dark:bg-[#1d2128] sm:gap-6 md:grid-cols-2 md:gap-10 lg:gap-16 hero:min-h-[756px] hero:gap-[12rem] hero:px-[38px] hero:pb-[36px] hero:pt-[36px] min-[1280px]:max-[1700px]:[gap:3rem!important]">
              <article className="flex min-h-[630px] flex-col max-[639px]:gap-[10px] rounded-[48px] border-[3px] border-[#3b82f6] bg-white px-[18px] py-[28px] dark:bg-[#181b21] hero:min-h-[676px] hero:rounded-[60px] hero:px-[0px] hero:pt-[31px] pb-0">
                <h2 className="font-['Inter',sans-serif] text-center text-[28px]  font-bold leading-[1.2] text-[#1a1a1a] dark:text-[#f3f4f6] sm:text-[30px]">
                  The Best of All Worlds.
                </h2>
                <div
                  className={`${insetCardClassName} mt-[38px] min-h-[218px] hero:mt-[38px] hero:mb-[38px] hero:min-h-[218px]`}
                >
                  <p className="font-['Inter',sans-serif] text-[18px] leading-[1.05] text-[#6a7480] dark:text-[#b3bcc8] sm:text-[20px] py-[2rem]">
                    <span className="font-extrabold italic text-[#d8562a]">
                      Built
                    </span>{' '}
                    for creators, travel vloggers, streamers, students, coders,
                    journalists, and personal use.
                  </p>
                </div>
                <div className={`${insetCardClassName} mt-auto min-h-[218px] hero:min-h-[218px]`}>
                  <p className="font-['Inter',sans-serif] text-[18px] leading-[1.05] text-[#6a7480] dark:text-[#b3bcc8] sm:text-[20px]">
                    Store your videos, recordings, outros, intros, documents,
                    that lab assignment, your C# files, and even your family
                    photos in one place.
                  </p>
                </div>

              </article>

              <article className="flex min-h-[630px] flex-col max-[639px]:gap-[10px] rounded-[48px] border-[3px] border-[#2b7e20] bg-white px-[18px] py-[28px] dark:bg-[#181b21] hero:min-h-[676px] hero:rounded-[60px] hero:px-[0px] hero:pt-[31px] pb-0">
                <h2 className="font-['Inter',sans-serif] text-center text-[28px] font-bold leading-[1.2] text-[#1a1a1a] dark:text-[#f3f4f6] sm:text-[32px] min-[1280px]:max-[1422px]:min-h-[70px]">
                  No boundaries.
                </h2>
                <div
                  className={`${insetCardClassName} mt-[38px] min-h-[218px] hero:mt-[38px] hero:mb-[38px] hero:min-h-[218px] space-y`}
                >
                  <p className="font-['Inter',sans-serif] text-[18px] leading-[1.05] text-[#6a7480] dark:text-[#b3bcc8] sm:text-[20px]">
                    <span className="font-extrabold italic text-[#d8562a]">
                      No
                    </span>{' '}
                    walled garden.{' '}
                    <span className="font-bold italic text-[#d8562a]">No</span>{' '}
                    closed ecosystem.{' '}
                    <span className="font-extrabold italic text-[#d8562a]">
                      No
                    </span>{' '}
                    corporate traps locking down your digital life.
                  </p>
                  <p className="mt-[24px] text-[18px] leading-[1.05] text-[#6a7480] dark:text-[#b3bcc8] sm:text-[20px]">
                    Take <span className="font-bold text-black dark:text-white">Cloud</span>
                    <span className="font-bold text-[#3b82f6]">Transfer</span>{' '}
                    with you wherever you go.
                  </p>
                </div>
                <div
                  className={`${insetCardClassName} mt-auto min-h-[222px] hero:min-h-[218px]`}
                >
                  <p className="font-['Inter',sans-serif] text-[18px] leading-[1.05] text-[#6a7480] dark:text-[#b3bcc8] sm:text-[20px]">
                    Whether you're hiking, on a road trip, or in a meeting,
                    download and upload your files without being locked into one
                    device, brand, or platform.
                  </p>
                </div>
              </article>
            </div>
          </section>
          <Footer />
        </div>
      </main>
    </Fragment>
  );
}
